// 카메라 개인 설정(렌더 카메라와 설정 화면이 함께 쓴다). 저장: localStorage 'dungeonhero.camera'.
// 줌(시야 거리)은 예전 저장 형식({ zoom })과 같은 키를 그대로 쓴다.
import { CAMERA_ZOOM } from './config';

/** 줌 가장 가까운 끝(숄더뷰 근접, 음수). 0..1은 예전 저장값 범위 그대로다 */
export const ZOOM_MIN = CAMERA_ZOOM.min;
/** 줌 값을 허용 범위로 맞춘다 */
export const clampZoom = (z: number): number => (Number.isFinite(z) ? Math.min(1, Math.max(ZOOM_MIN, z)) : 0);

/** 전투 연출 강도: 끔 / 약하게 / 보통 / 강하게 */
export type CineLevel = 'off' | 'low' | 'normal' | 'strong';
/** 타격 흔들림(카메라 펀치) 강도 */
export type ShakeLevel = 'off' | 'low' | 'normal';

export interface CameraPrefs {
  /** 0 = 기본 전투 구도, 1 = 넓은 전장, 0 아래(CAMERA_ZOOM.min까지) = 숄더뷰 근접 */
  zoom: number;
  /** 사용자 수평 회전(rad), -π 이상 π 미만 */
  yaw: number;
  /** 카메라 높이(피치 보정, 도). 음수 = 낮게, 양수 = 높게 */
  tilt: number;
  cine: CineLevel;
  shake: ShakeLevel;
  /** 터치 기기에서 두 손가락으로 줌 */
  pinch: boolean;
  /** 카메라 좌우 반전: 켜면 드래그 방향과 회전 방향이 기본의 반대(기본 = 왼쪽으로 끌면 시야가 왼쪽으로 돈다) */
  invertYaw: boolean;
}

export const CAMERA_PREFS_KEY = 'dungeonhero.camera';
/** 카메라 높이 보정 범위(도). 입자·바닥 효과가 기본 피치에 맞춰져 있어 크게 벌리지 않는다 */
export const TILT_RANGE = 6;
export const CINE_SCALE: Record<CineLevel, number> = { off: 0, low: 0.55, normal: 1, strong: 1.3 };
export const SHAKE_SCALE: Record<ShakeLevel, number> = { off: 0, low: 0.5, normal: 1 };
/** 설정이 바뀌면 window에 알린다(detail = CameraPrefs) */
export const CAMERA_PREFS_EVENT = 'dh-camera-prefs';

const CINES: readonly CineLevel[] = ['off', 'low', 'normal', 'strong'];
const SHAKES: readonly ShakeLevel[] = ['off', 'low', 'normal'];

export function defaultCameraPrefs(zoom = 0): CameraPrefs {
  return { zoom, yaw: 0, tilt: 0, cine: 'normal', shake: 'normal', pinch: true, invertYaw: false };
}

/** 여러 바퀴를 돌려도 내부 각도는 한 바퀴 안에 유지한다. 잘못된 저장값은 정면으로 복구한다. */
export function wrapCameraYaw(yaw: number): number {
  if (!Number.isFinite(yaw)) return 0;
  const turn = Math.PI * 2;
  return ((yaw + Math.PI) % turn + turn) % turn - Math.PI;
}

/** 저장 값을 읽어 범위를 맞춘다. 저장소가 없거나 깨졌으면 기본값 */
export function parseCameraPrefs(raw: string | null, zoomDefault = 0): CameraPrefs {
  const p = defaultCameraPrefs(zoomDefault);
  if (!raw) return p;
  try {
    const j = JSON.parse(raw) as Partial<Record<keyof CameraPrefs, unknown>>;
    if (typeof j.yaw === 'number') p.yaw = wrapCameraYaw(j.yaw);
    const z = Number(j.zoom);
    if (Number.isFinite(z)) p.zoom = Math.min(1, Math.max(ZOOM_MIN, z));
    const t = Number(j.tilt);
    if (Number.isFinite(t)) p.tilt = Math.min(TILT_RANGE, Math.max(-TILT_RANGE, t));
    if (typeof j.cine === 'string' && (CINES as readonly string[]).includes(j.cine)) p.cine = j.cine as CineLevel;
    if (typeof j.shake === 'string' && (SHAKES as readonly string[]).includes(j.shake)) p.shake = j.shake as ShakeLevel;
    if (typeof j.pinch === 'boolean') p.pinch = j.pinch;
    if (typeof j.invertYaw === 'boolean') p.invertYaw = j.invertYaw;
  } catch { /* 깨진 값: 기본값 */ }
  return p;
}

export function loadCameraPrefs(zoomDefault = 0): CameraPrefs {
  try {
    return parseCameraPrefs(typeof localStorage !== 'undefined' ? localStorage.getItem(CAMERA_PREFS_KEY) : null, zoomDefault);
  } catch { return defaultCameraPrefs(zoomDefault); }
}

/** 일부만 바꿔 저장하고 알린다. 바뀐 전체 설정을 돌려준다 */
export function updateCameraPrefs(patch: Partial<CameraPrefs>): CameraPrefs {
  const p = { ...loadCameraPrefs(), ...patch };
  p.yaw = wrapCameraYaw(p.yaw);
  p.zoom = Math.round(Math.min(1, Math.max(ZOOM_MIN, p.zoom)) * 100) / 100;
  p.tilt = Math.round(Math.min(TILT_RANGE, Math.max(-TILT_RANGE, p.tilt)) * 10) / 10;
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(CAMERA_PREFS_KEY, JSON.stringify(p)); } catch { /* 저장 실패는 무시 */ }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CAMERA_PREFS_EVENT, { detail: p }));
  return p;
}
